// GDPR data-deletion requests: the staff review queue's calls.
// The public submit/confirm calls live with the public pages, which post
// without a token.
import { apiGet, apiPost } from "./fetch";

export interface SuggestedMatch {
	person_id: number;
	name: string;
	headline: string | null;
	location: string | null;
	matched_on: string[];
	/** Exact identity (LinkedIn address, or an email/phone only they hold). */
	confident: boolean;
}

export type DeletionRequestStatus = "confirmed" | "completed" | "rejected";

export interface DeletionRequestRow {
	id: number;
	reference: string;
	email: string;
	name: string;
	linkedin_url: string | null;
	phone: string | null;
	details: string | null;
	status: DeletionRequestStatus;
	/** An operator entered this one for somebody who asked by phone or in
	 * person. Nothing is ever emailed for it, and there is no confirmation
	 * mail to show. */
	operator_created: boolean;
	created: string;
	confirmed_at: string;
	suggested_matches: SuggestedMatch[];
}

/** What an operator writes down for somebody who asked off-line. */
export interface OperatorRequestInput {
	name: string;
	email: string;
	linkedin_url: string;
	phone: string;
	details: string;
}

/** One mail the requester was sent, as they received it, rendered server-side. */
export interface DeletionEmail {
	kind: "confirmation" | "completed" | "rejected";
	subject: string;
	to: string;
	html: string;
	sent_at: string;
	/** What SES reported on the mail; null when no report came. */
	delivery: { status: "delivered" | "bounced" | "complaint"; at: string } | null;
}

export const fetchDeletionRequests = (): Promise<{
	results: DeletionRequestRow[];
}> =>
	apiGet("/api/v2/deletion-request-admin/") as Promise<{
		results: DeletionRequestRow[];
	}>;

/** Fired after any call that changes what waits for review, so the staff
 * banner recounts at once instead of on its next poll. */
export const DELETION_REQUESTS_CHANGED = "deletion-requests-changed";

const announceChange = <T>(result: T): T => {
	window.dispatchEvent(new Event(DELETION_REQUESTS_CHANGED));
	return result;
};

export const createDeletionRequest = (
	input: OperatorRequestInput
): Promise<DeletionRequestRow> =>
	(
		apiPost("/api/v2/deletion-request-admin/", input) as Promise<DeletionRequestRow>
	).then(announceChange);

/** Every mail the requester was sent, oldest first. */
export const fetchDeletionEmails = (id: number): Promise<DeletionEmail[]> =>
	apiGet(`/api/v2/deletion-request-admin/${id}/emails/`) as Promise<DeletionEmail[]>;

export const approveDeletionRequest = (
	id: number,
	personId: number | null
): Promise<{ message: string }> =>
	(
		apiPost(`/api/v2/deletion-request-admin/${id}/approve/`, {
			person_id: personId,
		}) as Promise<{ message: string }>
	).then(announceChange);

export const rejectDeletionRequest = (
	id: number,
	reason: string
): Promise<{ message: string }> =>
	(
		apiPost(`/api/v2/deletion-request-admin/${id}/reject/`, {
			reason,
		}) as Promise<{ message: string }>
	).then(announceChange);

/** Confirmed requests waiting for an operator — what the staff banner shows. */
export interface PendingDeletionRequests {
	count: number;
	oldest_confirmed_at: string | null;
}

export const fetchPendingDeletionRequests = (): Promise<PendingDeletionRequests> =>
	apiGet(
		"/api/v2/deletion-request-admin/pending/"
	) as Promise<PendingDeletionRequests>;
